import React, { useState } from 'react';
import Dropdown from '../ui/Dropdown';
import {
  buildSceneOptions,
  parseSceneList,
  scenesToDisplay,
  addScene,
  removeScene,
  normaliseSceneNumber,
  sceneOptionKey,
} from '../../utils/sceneOptions';

/**
 * The Scenes cell's multi-select, for the Breakdown's inline row editor.
 *
 * ```
 * Scenes  [12 ✕] [14 ✕]  ▾
 *         ┌────────────────────────┐
 *         │ ☑ 12   ☑ 14   ☐ 15     │   ← scene numbers already used
 *         │ ☐ 22A  ☐ 31            │      by gags in THIS department
 *         ├────────────────────────┤
 *         │ + Add scene number…    │
 *         └────────────────────────┘
 * ```
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔑 THE OPTIONS ARE HARVESTED, NOT CONFIGURED
 * ═══════════════════════════════════════════════════════════════════════════
 * **There is no master scene table.** `gag_scenes` is a join table, so the only
 * possible option list is the distinct `scene_number` values already used by
 * gags in this department — `GET /api/gags/scene-suggestions`. Product owner's
 * ruling, 2026-08-09. A scene the user types is simply USED; nothing is
 * registered anywhere, because there is nowhere to register it.
 *
 * The whole cluster is `helpers/supplierName.js` + `GET /api/suppliers/
 * name-suggestions` + `utils/supplierOptions.js` transposed. Read that one
 * before changing this.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 IT DECIDES NOTHING ABOUT PERMISSIONS
 * ═══════════════════════════════════════════════════════════════════════════
 * Scenes is a FIELD edit, gated by the same server-computed `can_edit` every
 * other row control is gated by. This component is only ever mounted for a row
 * the server marked editable, and there is **no client-side editability test in
 * this file and none may be added** — the rule `GagRowEditCell.js` and
 * `components/OrderInlineEditor.js` both carry.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY `ui/Dropdown` AND NOT A HAND-ROLLED POPOVER
 * ═══════════════════════════════════════════════════════════════════════════
 * It is the house disclosure primitive and already gets right every part of
 * this that is easy to get subtly wrong: outside-click close, Escape close with
 * focus RETURNED to the trigger, `aria-expanded`/`aria-haspopup`, and the
 * render-prop `children({ close })`. A fourth hand-rolled popover in this repo
 * would get one of those wrong.
 *
 * 📏 **It is also what lets the Scenes column keep its measured 120px** — the
 * trigger truncates, the panel does not, so this increment needed no
 * re-measurement of `constants/gagTableColumns.js`. That conclusion still
 * holds. **The REASON given for it did not.**
 *
 * 🚨 **THIS SAID *"the panel is absolutely positioned, so it is not
 * constrained by the cell width"* UNTIL 2026-08-09, AND THAT PREMISE WAS
 * FALSE — IT IS THE DEFECT, WRITTEN DOWN AS A REASSURANCE.** Absolute
 * positioning escapes an ancestor's LAYOUT, never its `overflow` CLIP: this
 * cell's `<td>` carries `truncate` (= `overflow: hidden`), so the panel was
 * clipped to a 120×49 box — 250px of its 257px height cut away. The owner saw
 * it as *"scene dropdown is hidden BEHIND the expanded card"*.
 *
 * 🕶️ The bracketed line was even sharper: it named *"whether the panel is
 * clipped by the table's one scroll region"* as an OWED BROWSER CHECK. The
 * check was owed, was not done, and when finally run came back **positive on
 * two clippers at once** — the `<td>` (always) and the region (near its
 * edges). **An owed browser check recorded and not performed is how this
 * shipped**; jsdom cannot see any of it. Fixed by `portal` on `ui/Dropdown`
 * (see the prop below), whose docblock carries the full measurements.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ THE CHIPS CARRY NO PER-CHIP ✕, AND THAT IS FORCED, NOT A SIMPLIFICATION
 * ═══════════════════════════════════════════════════════════════════════════
 * The agreed sketch showed `[12 ✕] [14 ✕] ▾` on the trigger row. The chips are
 * there; the ✕ is not, because **the trigger IS a `<button>` and a `<button>`
 * inside a `<button>` is invalid HTML** — browsers un-nest it, which breaks the
 * disclosure, and assistive technology cannot name either control. Rendering
 * the chip row BESIDE the trigger instead was the alternative and does not fit:
 * the Scenes column is 120px (`constants/gagTableColumns.js`, measured), which
 * is one trigger's width.
 *
 * **Removal is the checkbox in the panel** — the same control that adds, so
 * there is one way to change a selection rather than two. If the owner wants
 * the ✕ back, the honest way is a wider Scenes column with the chips outside
 * the trigger, which is a width decision and belongs with the drag increment.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⌨️ KEYBOARD — WHY THIS COMPONENT DOES NOT TOUCH IT
 * ═══════════════════════════════════════════════════════════════════════════
 * The row's Enter-saves / Escape-prompts handler must NOT fire while this
 * picker is open: Enter should tick an option and Escape should close the
 * picker. The row handler therefore tests for an open dropdown by reading
 * `aria-expanded` off the DOM — **the attribute `ui/Dropdown` already sets and
 * the accessibility tree already reads.**
 *
 * 🪤 It is deliberately NOT done with `stopPropagation` here. `ui/Dropdown`
 * closes from a **`document`-level** keydown listener while React attaches its
 * handlers to the root container, so a synthetic `stopPropagation` would stop
 * the native event ever reaching `document` and the picker would never close at
 * all — the opposite of the intent. See `GagGroupTable.js`'s `rowEditKeyDown`.
 */

const CHIP =
  'inline-flex items-center gap-0.5 max-w-full px-1 rounded text-xs ' +
  'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';

const TRIGGER =
  'w-full flex items-center gap-1 px-1.5 py-1 text-sm rounded-md ' +
  'border border-blue-300 dark:border-blue-600 ' +
  'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

const ADD_INPUT =
  'flex-1 min-w-0 px-2 py-1 text-sm rounded-md border border-gray-300 dark:border-gray-600 ' +
  'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

export default function GagSceneMultiSelect({
  gag,
  /** The draft's `scenes` value — the comma-joined display string. */
  value,
  /** Called with the new comma-joined display string. */
  onChange,
  /** Rows from GET /api/gags/scene-suggestions. */
  suggestions = [],
}) {
  const [typed, setTyped] = useState('');
  const selected = parseSceneList(value);
  const options = buildSceneOptions(suggestions, selected);

  // 🔑 Every write goes back out through `scenesToDisplay`, so the draft only
  // ever holds the CANONICAL form. The row's dirty test compares that against
  // the gag's own `scenes_display` normalised the same way, which is what stops
  // a reorder reading as a change.
  const commit = (list) => onChange(scenesToDisplay(list));

  const toggle = (scene) => {
    const isOn = selected.some((s) => sceneOptionKey(s) === sceneOptionKey(scene));
    commit(isOn ? removeScene(selected, scene) : addScene(selected, scene));
  };

  const commitTyped = () => {
    const scene = normaliseSceneNumber(typed);
    if (!scene) return;
    commit(addScene(selected, scene));
    setTyped('');
  };

  const label = (
    <span className="flex-1 min-w-0 flex items-center gap-0.5 overflow-hidden">
      {selected.length === 0 ? (
        <span className="text-gray-400 dark:text-gray-500 text-xs">No scenes</span>
      ) : (
        selected.map((s) => (
          <span key={sceneOptionKey(s)} className={`${CHIP} truncate`}>{s}</span>
        ))
      )}
    </span>
  );

  return (
    <Dropdown
      label={label}
      align="left"
      haspopup="dialog"
      triggerClassName={TRIGGER}
      triggerTestId={`gag-edit-scenes-${gag.id}`}
      panelClassName="w-64 p-2"
      // 🚨 REQUIRED HERE, AND IT IS A CLIP FIX RATHER THAN A Z-INDEX ONE.
      // Measured 2026-08-09: this cell's `<td>` carries `truncate`
      // (= `overflow: hidden`, to ellipse the read-only scene list inside
      // 120px), and the table sits in the ONE page-level scroll region. An
      // anchored panel is a descendant of BOTH, so both clip it — 250px of a
      // 257px panel was cut off, leaving a 7px sliver that reads as the panel
      // being "behind the expanded card". The panel's `z-50` was never the
      // problem: its stacking-context chain to <html> contains no intervening
      // context at all, and lifting ONLY the cell's overflow makes it fully
      // visible above the detail panel with no z-index change whatsoever.
      // **So do not try to fix a recurrence of this by raising a z-index.**
      // Full measurements and the control experiment: `ui/Dropdown`'s docblock.
      portal
      // ♿ The column heading is not programmatically associated with the cell,
      // so the control names itself and the gag it belongs to — the same
      // convention every other control in GagRowEditCell uses.
      title={selected.length ? `Scenes: ${selected.join(', ')}` : 'No scenes'}
      ariaLabel={`Scenes for ${gag.title}`}
    >
      {() => (
        <div>
          <div
            className="max-h-48 overflow-y-auto flex flex-wrap gap-1"
            data-testid={`gag-scene-options-${gag.id}`}
          >
            {options.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 px-1 py-2">
                No scene numbers used yet — add the first one below.
              </p>
            )}
            {options.map((o) => (
              <label
                key={o.key}
                className="inline-flex items-center gap-1 px-1.5 py-1 rounded cursor-pointer text-sm
                           text-gray-800 dark:text-gray-200
                           hover:bg-gray-100 dark:hover:bg-gray-700
                           focus-within:ring-2 focus-within:ring-blue-500"
              >
                <input
                  type="checkbox"
                  checked={o.selected}
                  onChange={() => toggle(o.scene)}
                  data-testid={`gag-scene-option-${gag.id}-${o.key}`}
                  aria-label={`Scene ${o.scene}`}
                  className="rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                />
                <span className="truncate">{o.scene}</span>
              </label>
            ))}
          </div>

          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 flex items-center gap-1">
            <input
              type="text"
              className={ADD_INPUT}
              value={typed}
              placeholder="Add scene number…"
              aria-label={`Add a scene number to ${gag.title}`}
              data-testid={`gag-scene-add-input-${gag.id}`}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                // Enter here ADDS the typed scene. It must never reach the row's
                // Enter-saves handler — but the row handler already skips any
                // event raised while a dropdown is open, so this only has to
                // stop the browser submitting anything.
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitTyped();
                }
              }}
            />
            <button
              type="button"
              className="px-2 py-1 text-sm rounded-md bg-blue-600 text-white
                         disabled:opacity-40 disabled:cursor-not-allowed
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              disabled={normaliseSceneNumber(typed) === ''}
              onClick={commitTyped}
              data-testid={`gag-scene-add-${gag.id}`}
              // 🔑 "Add" and not "Create": nothing is created. The scene is used
              // on this gag and appears in everyone else's harvest the moment
              // the row is saved.
              title="Add this scene number to the gag"
            >
              Add
            </button>
          </div>
        </div>
      )}
    </Dropdown>
  );
}
